import React, { useEffect, useState } from "react";
import { QrCode, CheckCircle, XCircle, Info, ArrowClockwise, Camera, Keyboard, Copy, ArrowSquareOut } from "@phosphor-icons/react";
import { Stat } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import QrScanner from "../../components/QrScanner";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";

export default function Checkin() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [info, setInfo] = useState(null); // checkin-info: code, pin, checkpoints
  const [checkpointId, setCheckpointId] = useState("");
  const [live, setLive] = useState(null);
  const [useCamera, setUseCamera] = useState(false);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    api.get("/organizer/events").then((r) => {
      const pub = r.data.events.filter((e) => e.status === "published");
      setEvents(pub);
      if (pub[0]) setEventId(pub[0].id);
    });
  }, []);

  const loadLive = (eid) => { if (eid) api.get(`/organizer/events/${eid}/live`).then((r) => setLive(r.data)); };
  const loadInfo = (eid) => {
    if (!eid) return;
    api.get(`/organizer/events/${eid}/checkin-info`).then((r) => {
      setInfo(r.data);
      if (r.data.checkpoints?.[0]) setCheckpointId(r.data.checkpoints[0].id);
    });
  };
  useEffect(() => { loadLive(eventId); loadInfo(eventId); }, [eventId]);

  const doScan = async (raw) => {
    if (!raw || !eventId || !checkpointId || scanning) return;
    setScanning(true);
    try {
      const { data } = await api.post("/organizer/checkin", { code: raw.trim(), event_id: eventId, checkpoint_id: checkpointId });
      setResult({ ok: true, ...data });
      if (data.status === "already_checked_in") toast.info(data.message);
      else toast.success(data.message);
      setCode(""); loadLive(eventId);
    } catch (e) {
      setResult({ ok: false, message: apiErr(e) });
      toast.error(apiErr(e));
    } finally { setTimeout(() => setScanning(false), 600); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied."); };

  return (
    <div className="max-w-3xl">
      <div className="overline mb-1">Event day</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Gate Check-in</h1>

      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Event</label>
          <Select value={eventId} onValueChange={setEventId}>
            <SelectTrigger data-testid="checkin-event"><SelectValue placeholder="Select event" /></SelectTrigger>
            <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Checkpoint</label>
          <Select value={checkpointId} onValueChange={setCheckpointId}>
            <SelectTrigger data-testid="checkin-checkpoint"><SelectValue placeholder="Select checkpoint" /></SelectTrigger>
            <SelectContent>{(info?.checkpoints || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {info && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div>Staff kiosk code: <button onClick={() => copy(info.checkin_code)} className="font-mono font-bold text-primary inline-flex items-center gap-1">{info.checkin_code} <Copy size={13} /></button></div>
          <div>PIN: <span className="font-mono font-bold">{info.checkin_pin}</span></div>
          <a href="/scan" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary font-semibold ml-auto">Open Checker portal <ArrowSquareOut size={14} /></a>
        </div>
      )}

      {live && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Stat label="Capacity" value={live.capacity} />
          <Stat label="Tickets Sold" value={live.tickets_sold} />
          <Stat label="Checked In" value={live.checked_in} accent />
          <Stat label="Remaining" value={live.remaining} />
        </div>
      )}
      {live?.checkpoints?.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {live.checkpoints.map((c, i) => <span key={i} className="px-3 py-1.5 rounded-full bg-muted text-sm">{c.name}: <b>{c.scanned}</b></span>)}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><QrCode size={22} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Scan ticket</h2></div>
          <Button variant="outline" size="sm" onClick={() => setUseCamera((c) => !c)} className="rounded-full" data-testid="toggle-camera">
            {useCamera ? <><Keyboard size={15} className="mr-1" /> Manual</> : <><Camera size={15} className="mr-1" /> Camera</>}
          </Button>
        </div>
        {useCamera ? (
          <QrScanner active={useCamera && !!eventId && !!checkpointId} onDetected={doScan} />
        ) : (
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doScan(code)} placeholder="Paste QR content or ticket code" className="font-mono" data-testid="checkin-code" />
            <Button onClick={() => doScan(code)} className="rounded-full px-6" data-testid="checkin-scan">Validate</Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-2">Repeat scans stay safe and show the original check-in status.</p>

        {result && (
          <div className={`mt-5 rounded-lg p-4 flex items-start gap-3 ${result.ok ? (result.status === "already_checked_in" ? "bg-sky-500/10 border border-sky-500/30" : "bg-emerald-500/10 border border-emerald-500/30") : "bg-red-500/10 border border-red-500/30"}`} data-testid="checkin-result">
            {result.ok ? (result.status === "already_checked_in" ? <Info weight="fill" className="text-sky-500 mt-0.5" size={22} /> : <CheckCircle weight="fill" className="text-emerald-600 mt-0.5" size={22} />) : <XCircle weight="fill" className="text-red-600 mt-0.5" size={22} />}
            <div>
              <div className="font-semibold">{result.message}</div>
              {result.ticket && <div className="text-sm text-muted-foreground">{result.ticket.category} · {result.ticket.holder || "Guest"}</div>}
            </div>
          </div>
        )}
      </div>
      <Button variant="ghost" onClick={() => loadLive(eventId)} className="mt-4"><ArrowClockwise size={16} className="mr-1.5" /> Refresh stats</Button>
    </div>
  );
}
